import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import ErrorDialog from '../components/ui/ErrorDialog';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { api, consumeSessionExpiredToast } from '../services/authService';

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  
  const emailValue = watch('email');

  // Autofill email from query parameter
  useEffect(() => {
    if (emailParam) {
      setValue('email', emailParam);
    }
  }, [emailParam, setValue]);

  // Toast after 401/403 redirect (full page reload clears in-memory toasts)
  useEffect(() => {
    if (consumeSessionExpiredToast()) {
      toast.error('Your session has expired. Please log in again.');
    }
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const result = await login(data.email, data.password);
      if (result.success) {
        toast.success('Login successful!');
        // Redirect to profile if password reset is required
        if (result.password_reset) {
          navigate('/profile');
        } else {
          navigate('/dashboard');
        }
      } else if (result.requires_2fa) {
        // Redirect to 2FA verification page
        navigate(`/verify-2fa?email=${encodeURIComponent(result.email)}`);
      } else {
        setErrorMessage(result.error || 'Login failed. Please check your credentials and try again.');
        setShowErrorDialog(true);
      }
    } catch (error) {
      setErrorMessage('An error occurred during login. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  // Format email to display name (same logic as other components)
  const formatNameFromEmail = (email) => {
    if (!email) return '';
    const localPart = email.split('@')[0];
    return localPart
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const handleResetPassword = async () => {
    const email = emailValue;
    if (!email) {
      setErrorMessage('Please enter an email address first.');
      setShowErrorDialog(true);
      setShowResetDialog(false);
      return;
    }

    setResetLoading(true);
    try {
      await api.post('/api/v2/auth/password-reset', { email });
      // Always show success message for security (don't reveal if user exists)
      toast.success('If the email exists, a password reset email has been sent.');
      setShowResetDialog(false);
    } catch (error) {
      // Always show success message for security (don't reveal if user exists)
      toast.success('If the email exists, a password reset email has been sent.');
      setShowResetDialog(false);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">The Cube</h1>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">Sign in to your account</h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-hairline rounded-lg placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 4,
                      message: 'Password must be at least 4 characters',
                    },
                  })}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="appearance-none block w-full pl-10 pr-10 py-2 border border-hairline rounded-lg placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 group relative flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#0062FB] hover:bg-[#0052E0] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0062FB] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!emailValue) {
                    setErrorMessage('Please enter an email address first.');
                    setShowErrorDialog(true);
                    return;
                  }
                  setShowResetDialog(true);
                }}
                disabled={loading || resetLoading}
                className="flex-1 group relative flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset Password
              </button>
            </div>
          </form>
        </div>
      </div>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Login Error"
        message={errorMessage}
      />

      <ConfirmDialog
        isOpen={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onConfirm={handleResetPassword}
        title="Reset Password"
        message={`Are you sure you want to reset the password for ${formatNameFromEmail(emailValue || '') || 'this user'}?`}
        confirmText="OK"
      />
    </div>
  );
}

export default Login;
